import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen,
  Coins,
  Rocket,
  TrendingUp,
  Shield,
  Receipt,
  Users,
  ChevronDown
} from 'lucide-react';
import './FAQModal.css';
import logo from '../images/logo.svg';

const FAQModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState({
    investmentBasics: true,
    fundraisingDetails: false,
    returns: false,
    technology: false,
    taxes: false,
    team: false
  });

  useEffect(() => {
    if (isOpen) {
      setOpenSections({
        investmentBasics: false,
        fundraisingDetails: false,
        returns: false,
        technology: false,
        taxes: false,
        team: false
      });
    }
  }, [isOpen]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content faq-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <img src={logo} alt="Sylents" className="faq-logo" />
        <p className="faq-welcome">{t('faqWelcome')}</p>
        <h2 className="faq-title">
          <BookOpen className="section-icon" size={32} />
          {t('faqTitle')}
        </h2>
        
        <div className="faq-sections">
          {/* Investment Basics */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('investmentBasics')}
              aria-expanded={openSections.investmentBasics}
            >
              <h3>
                <Coins className="section-icon" size={28} />
                {t('faqInvestmentBasicsTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.investmentBasics ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.investmentBasics ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqWhatAmIInvestingInQuestion')}</h4>
                <p>{t('faqWhatAmIInvestingInAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqVotingRightsQuestion')}</h4>
                <p>{t('faqVotingRightsAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqWhatIsFlexCoQuestion')}</h4>
                <p>{t('faqWhatIsFlexCoAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqTokenizedSharesQuestion')}</h4>
                <p>{t('faqTokenizedSharesAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqMinimumInvestmentQuestion')}</h4>
                <p>{t('faqMinimumInvestmentAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqFundsUsageQuestion')}</h4>
                <p>{t('faqFundsUsageAnswer')}</p>
                <ul>
                  <li>{t('faqFundsUsageNewHires')}</li>
                  <li>{t('faqFundsUsageMarketing')}</li>
                  <li>{t('faqFundsUsageRnd')}</li>
                  <li>{t('faqFundsUsageOperational')}</li>
                </ul>
              </div>
              <div className="faq-item">
                <h4>{t('faqHowToParticipateQuestion')}</h4>
                <p>{t('faqHowToParticipateAnswer')}</p>
              </div>
            </div>
          </section>

          {/* Fundraising Details */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('fundraisingDetails')}
              aria-expanded={openSections.fundraisingDetails}
            >
              <h3>
                <Rocket className="section-icon" size={28} />
                {t('faqFundraisingDetailsTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.fundraisingDetails ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.fundraisingDetails ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqPrivateVsPublicQuestion')}</h4>
                <p>{t('faqPrivateSaleDetails')}</p>
                <p>{t('faqPublicSaleDetails')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqSoftcapHardcapQuestion')}</h4>
                <p>{t('faqSoftcapDetails')}</p>
                <p>{t('faqHardcapDetails')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqSaleStartQuestion')}</h4>
                <p>{t('faqSaleStartAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqPaymentMethodsQuestion')}</h4>
              </div>
            </div>
          </section>

          {/* Returns */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('returns')}
              aria-expanded={openSections.returns}
            >
              <h3>
                <TrendingUp className="section-icon" size={28} />
                {t('faqReturnsTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.returns ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.returns ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqDividendsQuestion')}</h4>
                <p>{t('faqDividendsAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqSellSharesQuestion')}</h4>
                <p>{t('faqSellSharesAnswer')}</p>
              </div>
            </div>
          </section>

          {/* Technology */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('technology')}
              aria-expanded={openSections.technology}
            >
              <h3>
                <Shield className="section-icon" size={28} />
                {t('faqTechnologyTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.technology ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.technology ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqWalletQuestion')}</h4>
                <p>{t('faqWalletAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqBlockchainQuestion')}</h4>
                <p>{t('faqBlockchainAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqInvestmentSecurityQuestion')}</h4>
                <p>{t('faqInvestmentSecurityAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqKycQuestion')}</h4>
                <p>{t('faqKycAnswer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('faqSoftcapNotReachedQuestion')}</h4>
                <p>{t('faqSoftcapNotReachedAnswer')}</p>
              </div>
            </div>
          </section>

          {/* Taxes */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('taxes')}
              aria-expanded={openSections.taxes}
            >
              <h3>
                <Receipt className="section-icon" size={28} />
                {t('faqTaxesTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.taxes ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.taxes ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqTaxesQuestion')}</h4>
                <p>{t('faqTaxesAnswer')}</p>
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="faq-section">
            <button 
              className="faq-section-header"
              onClick={() => toggleSection('team')}
              aria-expanded={openSections.team}
            >
              <h3>
                <Users className="section-icon" size={28} />
                {t('faqTeamTitle')}
              </h3>
              <ChevronDown className={`section-chevron ${openSections.team ? 'open' : ''}`} />
            </button>
            <div className={`faq-section-content ${openSections.team ? 'open' : ''}`}>
              <div className="faq-item">
                <h4>{t('faqWhoIsBehindQuestion')}</h4>
                <ul>
                  <li>{t('faqTeamMemberHilmar')}</li>
                  <li>{t('faqTeamMemberHans')}</li>
                  <li>{t('faqTeamMemberMarkus')}</li>
                </ul>
              </div>
              <div className="faq-item">
                <h4>{t('faqGoalsQuestion')}</h4>
                <p>{t('faqGoalsAnswer')}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FAQModal; 